/**
 * Production API origin.
 *
 * `vite build --mode main` loads `.env.main` and inlines `VITE_API_BASE_URL`.
 * Local `vite` / tests do not load that file, so they keep the page origin.
 */
export function configuredApiOrigin(
  value: unknown = import.meta.env.VITE_API_BASE_URL,
): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  if (!/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(value.trim())) return null;
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (
    (url.protocol !== 'http:' && url.protocol !== 'https:') ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    url.pathname !== '/'
  ) {
    return null;
  }
  return url.origin;
}

/** Backend origin for live reads. Falls back to the page origin when no API URL is configured. */
export function apiBaseOrigin(): string {
  return configuredApiOrigin() ?? (typeof window === 'undefined' ? '' : window.location.origin);
}

/**
 * The exact origin from `VITE_API_BASE_URL`, when `baseOrigin` is that root URL.
 * Returns null for every other value so existing loopback checks stay in force.
 */
export function configuredTransportOrigin(
  baseOrigin: string,
  configured: string | null = configuredApiOrigin(),
): string | null {
  if (!configured || !/^https?:\/\/[^/?#\\\s@]+\/?$/i.test(baseOrigin)) return null;
  let url: URL;
  try {
    url = new URL(baseOrigin);
  } catch {
    return null;
  }
  if (url.username || url.password || url.search || url.hash || url.pathname !== '/') return null;
  return url.origin === configured ? url.origin : null;
}

/** Explicit opt-in for a built synthetic UI. This never enables backend management authority. */
export function syntheticDemoPortalsEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_SYNTHETIC_DEMO_PORTALS === 'true';
}
